import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { buildApiUrl } from '@/config/api'
import { VENDOR_INDUSTRIES, VENDOR_INDUSTRY_SUBCATEGORIES } from '../constants/industries'
import {
  Building2,
  Linkedin,
  Globe,
  Users,
  Award,
  Languages,
  Tag,
  Plus,
  X,
  Save,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react'

interface VendorProfile {
  id?: string
  companyName: string
  tagline?: string
  description: string
  industry: string
  services: string[]
  location?: string
  website?: string
  linkedinUrl?: string
  phone?: string
  email?: string
  foundedYear?: string
  companySize?: string
  certifications: string[]
  languages: string[]
  keywords: string[]
  logo?: string
  coverImage?: string
  gallery: string[]
  isPublic: boolean
  linkedinCompanyId?: string
  slug?: string
}

export default function VendorProfilePage() {
  const [profile, setProfile] = useState<VendorProfile>({
    companyName: '',
    description: '',
    industry: '',
    services: [],
    certifications: [],
    languages: [],
    keywords: [],
    gallery: [],
    isPublic: true
  })
  
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newService, setNewService] = useState('')
  const [newCertification, setNewCertification] = useState('')
  const [newLanguage, setNewLanguage] = useState('')
  const [newKeyword, setNewKeyword] = useState('')
  const [linkedinImportLoading, setLinkedinImportLoading] = useState(false)
  const [showLinkedInModal, setShowLinkedInModal] = useState(false)
  const [linkedinCompanyId, setLinkedinCompanyId] = useState('')
  const [linkedinError, setLinkedinError] = useState('')
  const [publishLoading, setPublishLoading] = useState(false)
  const [selectedIndustryCategory, setSelectedIndustryCategory] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [scrapingWebsite, setScrapingWebsite] = useState(false)
  const [scrapingLinkedIn, setScrapingLinkedIn] = useState(false)
  const [scrapeError, setScrapeError] = useState<string | null>(null)

  useEffect(() => {
    console.log('🚀 VendorProfile component mounted, loading profile...')
    loadExistingProfile()
  }, [])

  const loadExistingProfile = async () => {
    try {
      console.log('🔄 Loading existing vendor profile...')
      setLoading(true)
      const response = await fetch(buildApiUrl('/api/vendor-profiles/me/profile'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      console.log('📡 Profile response:', response.status, response.statusText)
      
      if (response.ok) {
        const data = await response.json()
        console.log('📊 Profile data received:', data)
        if (data.success && data.data) {
          setProfile(data.data)
          
          // Set the industry category based on existing industry
          const existingIndustry = data.data.industry
          if (existingIndustry) {
            // Check if it's a subcategory first
            for (const [category, subcategories] of Object.entries(VENDOR_INDUSTRY_SUBCATEGORIES)) {
              if (subcategories.includes(existingIndustry)) {
                setSelectedIndustryCategory(category)
                break
              }
            }
            // If not found in subcategories, check if it's a main category
            if (!selectedIndustryCategory && VENDOR_INDUSTRIES.includes(existingIndustry as any)) {
              setSelectedIndustryCategory(existingIndustry)
            }
          }
          
          console.log('✅ Existing profile loaded')
        } else {
          console.log('ℹ️ No existing profile found (data is null), loading company data...')
          // No existing profile, try to load company data for pre-population
          await loadCompanyData()
        }
      } else if (response.status === 404) {
        console.log('ℹ️ No existing profile found (404), loading company data...')
        // No existing profile, try to load company data for pre-population
        await loadCompanyData()
      } else {
        const errorData = await response.json()
        console.log('❌ Profile error:', errorData)
      }
    } catch (error) {
      console.error('❌ Error loading profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadCompanyData = async () => {
    try {
      console.log('🔄 Loading company data...')
      const response = await fetch(buildApiUrl('/api/vendor-profiles/me/company-data'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
      
      console.log('📡 Company data response:', response.status, response.statusText)
      
      if (response.ok) {
        const data = await response.json()
        console.log('📊 Company data received:', data)
        if (data.success && data.data) {
          // Pre-populate profile with company data
          setProfile(prev => ({
            ...prev,
            companyName: data.data.companyName,
            description: data.data.description,
            industry: data.data.industry,
            location: data.data.location,
            website: data.data.website,
            linkedinUrl: data.data.linkedinUrl,
            foundedYear: data.data.foundedYear,
            companySize: data.data.companySize,
          }))
          console.log('✅ Profile updated with company data')
        }
      } else {
        const errorData = await response.json()
        console.log('❌ Company data error:', errorData)
      }
    } catch (error) {
      console.error('❌ Error loading company data:', error)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setSaveError(null) // Clear previous errors
      
      // Log the data being sent for debugging
      console.log('🚀 Saving vendor profile:', profile)
      
      const url = profile.id ? buildApiUrl('/api/vendor-profiles/me') : buildApiUrl('/api/vendor-profiles')
      const method = profile.id ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(profile)
      })
      
      const data = await response.json()
      console.log('📡 Save response:', data)
      
      if (data.success) {
        setProfile(data.data)
        alert('Profile saved successfully!')
      } else {
        // Show detailed validation errors if available
        if (data.errors && Array.isArray(data.errors)) {
          const errorMessages = data.errors.map((error: any) => {
            const field = error.path?.join('.') || 'Unknown field'
            return `${field}: ${error.message}`
          }).join(', ')
          setSaveError(`Validation errors: ${errorMessages}`)
        } else {
          setSaveError(data.message || 'Unknown error occurred while saving')
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error)
      setSaveError('Network error occurred while saving. Please check your connection and try again.')
    } finally {
      setSaving(false)
    }
  }

  const handlePublishToggle = async () => {
    if (!profile.id) {
      alert('Please save your profile first before publishing.')
      return
    }

    try {
      setPublishLoading(true)
      
      const response = await fetch(buildApiUrl('/api/vendor-profiles/me/publish'), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ isPublic: !profile.isPublic })
      })
      
      const data = await response.json()
      
      if (data.success) {
        setProfile(data.data)
        alert(data.message)
      } else {
        alert('Error updating publish status: ' + data.message)
      }
    } catch (error) {
      console.error('Error updating publish status:', error)
      alert('Error updating publish status')
    } finally {
      setPublishLoading(false)
    }
  }

  const handleLinkedInImport = async () => {
    if (!linkedinCompanyId.trim()) {
      setLinkedinError('Please enter a company URL or ID')
      return
    }

    try {
      setLinkedinImportLoading(true)
      setLinkedinError('')
      
      console.log('🔄 Starting LinkedIn import with company ID:', linkedinCompanyId)
      
      const response = await fetch(buildApiUrl('/api/vendor-profiles/me/linkedin-import'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          companyIdentifier: linkedinCompanyId.trim()
        })
      })
      
      console.log('📡 LinkedIn import response:', response.status, response.statusText)
      
      const data = await response.json()
      console.log('📊 LinkedIn import data:', data)
      
      if (data.success) {
        setProfile(prev => ({
          ...prev,
          ...data.data
        }))
        setShowLinkedInModal(false)
        setLinkedinCompanyId('')
        alert('✅ LinkedIn company data imported successfully!\n\nYour vendor profile has been updated with the latest information from LinkedIn.')
      } else {
        if (data.requiresLinkedInAuth) {
          setLinkedinError('Please log in with LinkedIn first to import company data. You can do this from your Profile page by clicking "Sync LinkedIn".')
        } else {
          setLinkedinError(data.message || 'Failed to import LinkedIn data')
        }
      }
    } catch (error) {
      console.error('❌ Error importing LinkedIn data:', error)
      setLinkedinError('Network error. Please check your connection and try again.')
    } finally {
      setLinkedinImportLoading(false)
    }
  }

  const addItem = (field: keyof VendorProfile, value: string) => {
    if (!value.trim()) return
    
    setProfile(prev => ({
      ...prev,
      [field]: [...(prev[field] as string[]), value.trim()]
    }))
    
    // Clear the input
    if (field === 'services') setNewService('')
    if (field === 'certifications') setNewCertification('')
    if (field === 'languages') setNewLanguage('')
    if (field === 'keywords') setNewKeyword('')
  }

  const removeItem = (field: keyof VendorProfile, index: number) => {
    setProfile(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).filter((_, i) => i !== index)
    }))
  }

  const handleScrapeWebsite = async () => {
    const websiteUrl = profile.website?.trim()
    if (!websiteUrl) {
      setScrapeError('Please enter a website URL first')
      return
    }

    try {
      setScrapingWebsite(true)
      setScrapeError(null)

      const response = await fetch(buildApiUrl('/api/vendor-profiles/me/scrape'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          url: websiteUrl,
          type: 'website'
        })
      })

      const data = await response.json()

      if (data.success) {
        // Merge scraped data with existing profile, preserving user's input
        setProfile(prev => ({
          ...prev,
          companyName: prev.companyName || data.data.companyName || '',
          tagline: prev.tagline || data.data.tagline || '',
          description: prev.description || data.data.description || '',
          industry: prev.industry || data.data.industry || '',
          location: prev.location || data.data.location || '',
          website: prev.website || data.data.website || '',
          linkedinUrl: prev.linkedinUrl || data.data.linkedinUrl || '',
          foundedYear: prev.foundedYear || data.data.foundedYear || '',
          companySize: prev.companySize || data.data.companySize || '',
          services: [...new Set([...prev.services, ...(data.data.services || [])])],
          keywords: [...new Set([...prev.keywords, ...(data.data.keywords || [])])],
          logo: prev.logo || data.data.logo || ''
        }))

        // Update industry category if industry was found
        if (data.data.industry && !selectedIndustryCategory) {
          const foundIndustry = data.data.industry
          for (const [category, subcategories] of Object.entries(VENDOR_INDUSTRY_SUBCATEGORIES)) {
            if (subcategories.includes(foundIndustry)) {
              setSelectedIndustryCategory(category)
              break
            }
          }
          if (!selectedIndustryCategory && VENDOR_INDUSTRIES.includes(foundIndustry as any)) {
            setSelectedIndustryCategory(foundIndustry)
          }
        }

        alert('✅ Website data scraped successfully! Review and adjust the prefilled information.')
      } else {
        setScrapeError(data.message || 'Failed to scrape website data')
      }
    } catch (error) {
      console.error('Error scraping website:', error)
      setScrapeError('Network error. Please check your connection and try again.')
    } finally {
      setScrapingWebsite(false)
    }
  }

  const handleScrapeLinkedIn = async () => {
    const linkedInUrl = profile.linkedinUrl?.trim()
    if (!linkedInUrl) {
      setScrapeError('Please enter a LinkedIn company URL first')
      return
    }

    try {
      setScrapingLinkedIn(true)
      setScrapeError(null)

      const response = await fetch(buildApiUrl('/api/vendor-profiles/me/scrape'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          url: linkedInUrl,
          type: 'linkedin'
        })
      })

      const data = await response.json()

      if (data.success) {
        // Merge scraped data with existing profile, preserving user's input
        setProfile(prev => ({
          ...prev,
          companyName: prev.companyName || data.data.companyName || '',
          tagline: prev.tagline || data.data.tagline || '',
          description: prev.description || data.data.description || '',
          industry: prev.industry || data.data.industry || '',
          location: prev.location || data.data.location || '',
          website: prev.website || data.data.website || '',
          linkedinUrl: prev.linkedinUrl || data.data.linkedinUrl || '',
          foundedYear: prev.foundedYear || data.data.foundedYear || '',
          companySize: prev.companySize || data.data.companySize || '',
          services: [...new Set([...prev.services, ...(data.data.services || [])])],
          keywords: [...new Set([...prev.keywords, ...(data.data.keywords || [])])],
          logo: prev.logo || data.data.logo || ''
        }))

        // Update industry category if industry was found
        if (data.data.industry && !selectedIndustryCategory) {
          const foundIndustry = data.data.industry
          for (const [category, subcategories] of Object.entries(VENDOR_INDUSTRY_SUBCATEGORIES)) {
            if (subcategories.includes(foundIndustry)) {
              setSelectedIndustryCategory(category)
              break
            }
          }
          if (!selectedIndustryCategory && VENDOR_INDUSTRIES.includes(foundIndustry as any)) {
            setSelectedIndustryCategory(foundIndustry)
          }
        }

        alert('✅ LinkedIn data scraped successfully! Review and adjust the prefilled information.')
      } else {
        setScrapeError(data.message || 'Failed to scrape LinkedIn data')
      }
    } catch (error) {
      console.error('Error scraping LinkedIn:', error)
      setScrapeError('Network error. Please check your connection and try again.')
    } finally {
      setScrapingLinkedIn(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {profile.id ? 'Edit Vendor Profile' : 'Create Vendor Profile'}
          </h1>
          <p className="text-gray-600">
            Showcase your company and services to potential clients
          </p>
        </div>

        <div className="space-y-6">
          {/* LinkedIn Import Section */}
          {!profile.id && (
            <Card className="border-blue-200 bg-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center text-blue-700">
                  <Linkedin className="w-5 h-5 mr-2" />
                  Quick Start with LinkedIn
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-600 mb-4">
                  Import your company information from LinkedIn to get started quickly.
                </p>
                <Button 
                  onClick={() => setShowLinkedInModal(true)}
                  disabled={linkedinImportLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {linkedinImportLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Linkedin className="w-4 h-4 mr-2" />
                  )}
                  Import from LinkedIn
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Basic Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center">
                  <Building2 className="w-5 h-5 mr-2" />
                  Basic Information
                </CardTitle>
                <Button
                  onClick={loadCompanyData}
                  variant="outline"
                  size="sm"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />}
                  Load from Company
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <Input
                    value={profile.companyName}
                    onChange={(e) => setProfile(prev => ({ ...prev, companyName: e.target.value }))}
                    placeholder="Enter your company name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tagline
                  </label>
                  <Input
                    value={profile.tagline || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, tagline: e.target.value }))}
                    placeholder="Brief company tagline"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Description *
                </label>
                <Textarea
                  value={profile.description}
                  onChange={(e) => setProfile(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your company, services, and what makes you unique"
                  rows={4}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Category *
                  </label>
                  <select
                    value={selectedIndustryCategory}
                    onChange={(e) => {
                      const category = e.target.value
                      setSelectedIndustryCategory(category)
                      // If no subcategories available, set the category as the industry
                      if (!VENDOR_INDUSTRY_SUBCATEGORIES[category]) {
                        setProfile(prev => ({ ...prev, industry: category }))
                      } else {
                        // Clear the industry if subcategories are available
                        setProfile(prev => ({ ...prev, industry: '' }))
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                  >
                    <option value="">Select an industry category...</option>
                    {VENDOR_INDUSTRIES.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedIndustryCategory && VENDOR_INDUSTRY_SUBCATEGORIES[selectedIndustryCategory] && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Industry Specialization *
                    </label>
                    <select
                      value={profile.industry}
                      onChange={(e) => setProfile(prev => ({ ...prev, industry: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      required
                    >
                      <option value="">Select a specialization...</option>
                      {VENDOR_INDUSTRY_SUBCATEGORIES[selectedIndustryCategory].map((subcategory) => (
                        <option key={subcategory} value={subcategory}>
                          {subcategory}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <Input
                    value={profile.location || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="City, Country"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Services */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Tag className="w-5 h-5 mr-2" />
                Services Offered
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder="Add a service (e.g., Web Development, Consulting)"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('services', newService)}
                  />
                  <Button onClick={() => addItem('services', newService)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.services.map((service, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      {service}
                      <button
                        onClick={() => removeItem('services', index)}
                        className="ml-2 text-blue-600 hover:text-blue-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {scrapeError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {scrapeError}
                  <button
                    onClick={() => setScrapeError(null)}
                    className="float-right text-red-400 hover:text-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={profile.website || ''}
                      onChange={(e) => setProfile(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="https://yourcompany.com"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleScrapeWebsite}
                      disabled={scrapingWebsite || !profile.website?.trim()}
                      variant="outline"
                      size="sm"
                      title="Scrape company data from website"
                    >
                      {scrapingWebsite ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Globe className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your website URL and click the icon to auto-fill company information
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LinkedIn URL
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={profile.linkedinUrl || ''}
                      onChange={(e) => setProfile(prev => ({ ...prev, linkedinUrl: e.target.value }))}
                      placeholder="https://linkedin.com/company/yourcompany"
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      onClick={handleScrapeLinkedIn}
                      disabled={scrapingLinkedIn || !profile.linkedinUrl?.trim()}
                      variant="outline"
                      size="sm"
                      title="Scrape company data from LinkedIn"
                    >
                      {scrapingLinkedIn ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Linkedin className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter your LinkedIn company URL and click the icon to auto-fill company information
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone
                  </label>
                  <Input
                    value={profile.phone || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <Input
                    value={profile.email || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@yourcompany.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Company Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Founded Year
                  </label>
                  <Input
                    value={profile.foundedYear || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, foundedYear: e.target.value }))}
                    placeholder="2020"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Size
                  </label>
                  <Input
                    value={profile.companySize || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, companySize: e.target.value }))}
                    placeholder="e.g., 1-10 employees, 11-50 employees"
                  />
                </div>
              </div>

              {/* Certifications */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Certifications
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newCertification}
                    onChange={(e) => setNewCertification(e.target.value)}
                    placeholder="Add a certification"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('certifications', newCertification)}
                  />
                  <Button onClick={() => addItem('certifications', newCertification)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.certifications.map((cert, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
                    >
                      <Award className="w-3 h-3 mr-1" />
                      {cert}
                      <button
                        onClick={() => removeItem('certifications', index)}
                        className="ml-2 text-green-600 hover:text-green-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Languages */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Languages
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newLanguage}
                    onChange={(e) => setNewLanguage(e.target.value)}
                    placeholder="Add a language"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('languages', newLanguage)}
                  />
                  <Button onClick={() => addItem('languages', newLanguage)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.languages.map((language, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800"
                    >
                      <Languages className="w-3 h-3 mr-1" />
                      {language}
                      <button
                        onClick={() => removeItem('languages', index)}
                        className="ml-2 text-purple-600 hover:text-purple-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Keywords */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keywords (for search optimization)
                </label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="Add a keyword"
                    onKeyPress={(e) => e.key === 'Enter' && addItem('keywords', newKeyword)}
                  />
                  <Button onClick={() => addItem('keywords', newKeyword)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                    >
                      {keyword}
                      <button
                        onClick={() => removeItem('keywords', index)}
                        className="ml-2 text-gray-600 hover:text-gray-800"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Status & Actions */}
          {profile.id ? (
            <Card className={`border-2 ${profile.isPublic ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {profile.isPublic ? (
                      <Eye className="w-5 h-5 text-green-600" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-yellow-600" />
                    )}
                    <div>
                      <h3 className={`font-medium ${profile.isPublic ? 'text-green-800' : 'text-yellow-800'}`}>
                        {profile.isPublic ? 'Profile is Published' : 'Profile is Private'}
                      </h3>
                      <p className={`text-sm ${profile.isPublic ? 'text-green-600' : 'text-yellow-600'}`}>
                        {profile.isPublic 
                          ? 'Your profile is visible in the vendor spotlight and can be found by buyers.'
                          : 'Your profile is private and not visible to buyers. Publish it to start receiving inquiries.'
                        }
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handlePublishToggle}
                    disabled={publishLoading}
                    variant={profile.isPublic ? "outline" : "default"}
                    className={profile.isPublic ? "border-green-300 text-green-700 hover:bg-green-100" : "bg-green-600 hover:bg-green-700"}
                  >
                    {publishLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : profile.isPublic ? (
                      <EyeOff className="w-4 h-4 mr-2" />
                    ) : (
                      <Eye className="w-4 h-4 mr-2" />
                    )}
                    {publishLoading ? 'Updating...' : (profile.isPublic ? 'Unpublish' : 'Publish Profile')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-blue-200 bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center space-x-3">
                  <EyeOff className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="font-medium text-blue-800">
                      Profile Not Saved Yet
                    </h3>
                    <p className="text-sm text-blue-600">
                      Save your profile first, then you'll be able to publish it to the vendor spotlight for buyers to discover.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Display */}
          {saveError && (
            <Card className="border-2 border-red-200 bg-red-50">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-3">
                  <div className="w-5 h-5 text-red-600 mt-0.5">
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-red-800 mb-1">
                      Unable to Save Profile
                    </h3>
                    <p className="text-sm text-red-700">
                      {saveError}
                    </p>
                  </div>
                  <button
                    onClick={() => setSaveError(null)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <div className="flex justify-end space-x-4">
            {profile.id && profile.isPublic && (
              <Button
                variant="outline"
                onClick={() => window.open(`/vendor-spotlight/${profile.slug}`, '_blank')}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Public Profile
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !profile.companyName || !profile.description || !profile.industry}
              className="bg-primary hover:bg-primary/90"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        </div>
      </div>

      {/* LinkedIn Import Modal */}
      {showLinkedInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Linkedin className="w-5 h-5 mr-2 text-blue-600" />
              Import from LinkedIn
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company URL or ID
                </label>
                <Input
                  type="text"
                  placeholder="https://www.linkedin.com/company/your-company"
                  value={linkedinCompanyId}
                  onChange={(e) => setLinkedinCompanyId(e.target.value)}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Examples: https://www.linkedin.com/company/your-company, your-company-name, or 123456789
                </p>
              </div>

              {linkedinError && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded">
                  {linkedinError}
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  onClick={handleLinkedInImport}
                  disabled={linkedinImportLoading || !linkedinCompanyId.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  {linkedinImportLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Linkedin className="w-4 h-4 mr-2" />
                      Import
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setShowLinkedInModal(false)
                    setLinkedinCompanyId('')
                    setLinkedinError('')
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
